if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.dialog = this.querySelector('.product-media-modal__dialog');
        this.content = this.querySelector('.product-media-modal__content');
        this.currentIndex = 0;
        this.mediaElements = [];

        this.initControls();
      }

      initControls() {
        if (!this.dialog) return;

        // Prevent clicking inside modal content or controls from triggering ModalDialog's pointerup hide
        this.dialog.addEventListener('pointerup', (event) => {
          if (
            event.target.closest(
              '.product-media-modal__arrow, .product-media-modal__counter, .product-media-modal__content img, deferred-media, product-model'
            )
          ) {
            event.stopPropagation();
          }
        });

        // Close when clicking directly on the empty backdrop
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog || event.target === this.content) {
            this.hide();
          }
        });

        const nextBtn = this.querySelector('.product-media-modal__arrow--next');
        const prevBtn = this.querySelector('.product-media-modal__arrow--prev');

        if (nextBtn && !nextBtn.dataset.bound) {
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.nextMedia();
          });
          nextBtn.dataset.bound = 'true';
        }

        if (prevBtn && !prevBtn.dataset.bound) {
          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.prevMedia();
          });
          prevBtn.dataset.bound = 'true';
        }

        // Keyboard navigation: Left/Right arrow keys and ESC to close
        if (!this.dataset.keyBound) {
          window.addEventListener('keydown', (e) => {
            if (!this.hasAttribute('open')) return;
            if (e.key === 'Escape' || e.code === 'Escape' || e.keyCode === 27) {
              e.preventDefault();
              this.hide();
              return;
            }
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              this.nextMedia();
            } else if (e.key === 'ArrowLeft') {
              e.preventDefault();
              this.prevMedia();
            }
          });
          this.dataset.keyBound = 'true';
        }

        // Swipe & drag gestures (touch swipe + mouse drag swipe + trackpad horizontal wheel)
        if (this.content && !this.content.dataset.swipeBound) {
          let startX = 0;
          let startY = 0;
          let isDragging = false;
          let startTime = 0;

          const onStart = (clientX, clientY) => {
            startX = clientX;
            startY = clientY;
            startTime = Date.now();
            isDragging = true;
          };

          const onEnd = (clientX, clientY) => {
            if (!isDragging) return;
            isDragging = false;
            const diffX = clientX - startX;
            const diffY = clientY - startY;

            // Horizontal swipe with minimum distance threshold
            if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
              if (diffX < 0) {
                this.nextMedia();
              } else {
                this.prevMedia();
              }
            }
          };

          // Touch events
          this.content.addEventListener(
            'touchstart',
            (e) => {
              if (e.touches && e.touches[0]) onStart(e.touches[0].clientX, e.touches[0].clientY);
            },
            { passive: true }
          );

          this.content.addEventListener(
            'touchend',
            (e) => {
              if (e.changedTouches && e.changedTouches[0]) onEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
            },
            { passive: true }
          );

          // Pointer drag events for desktop mouse/trackpad swipe
          this.content.addEventListener('pointerdown', (e) => {
            if (e.pointerType === 'mouse') {
              onStart(e.clientX, e.clientY);
            }
          });

          this.content.addEventListener('pointerup', (e) => {
            if (e.pointerType === 'mouse') {
              onEnd(e.clientX, e.clientY);
            }
          });

          // Horizontal wheel / trackpad swipe
          let wheelDebounce = 0;
          this.content.addEventListener(
            'wheel',
            (e) => {
              if (Math.abs(e.deltaX) > 35 && Date.now() - wheelDebounce > 350) {
                wheelDebounce = Date.now();
                if (e.deltaX > 0) {
                  this.nextMedia();
                } else {
                  this.prevMedia();
                }
              }
            },
            { passive: true }
          );

          this.content.dataset.swipeBound = 'true';
        }
      }

      show(opener) {
        // Prevent opening on mobile devices (< 750px)
        if (window.innerWidth <= 749) return;

        super.show(opener);
        this.initControls();
        const mediaId = opener ? opener.getAttribute('data-media-id') : null;
        this.setActiveMediaById(mediaId);
      }

      hide() {
        super.hide();
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      setActiveMediaById(mediaId) {
        this.mediaElements = this.getMediaItems();
        let targetIndex = 0;
        if (mediaId && this.mediaElements.length > 0) {
          const foundIndex = this.mediaElements.findIndex((el) => el.getAttribute('data-media-id') === mediaId);
          if (foundIndex !== -1) targetIndex = foundIndex;
        }
        this.setActiveMediaIndex(targetIndex);
      }

      setActiveMediaIndex(index) {
        this.mediaElements = this.getMediaItems();
        if (this.mediaElements.length === 0) return;

        const count = this.mediaElements.length;
        this.currentIndex = ((index % count) + count) % count;

        this.mediaElements.forEach((el, idx) => {
          if (idx === this.currentIndex) {
            el.classList.add('active');
            const template = el.querySelector('template');
            const content = template ? template.content : null;
            if (el.nodeName === 'DEFERRED-MEDIA' && content && content.querySelector('.js-youtube')) {
              el.loadContent();
            }
          } else {
            el.classList.remove('active');
          }
        });

        this.updateUI();
      }

      nextMedia() {
        this.setActiveMediaIndex(this.currentIndex + 1);
      }

      prevMedia() {
        this.setActiveMediaIndex(this.currentIndex - 1);
      }

      updateUI() {
        const nextBtn = this.querySelector('.product-media-modal__arrow--next');
        const prevBtn = this.querySelector('.product-media-modal__arrow--prev');
        const counter = this.querySelector('.product-media-modal__counter');
        const currentEl = this.querySelector('.product-media-modal__counter-current');
        const totalEl = this.querySelector('.product-media-modal__counter-total');

        const total = this.mediaElements ? this.mediaElements.length : 1;

        if (total <= 1) {
          if (nextBtn) nextBtn.style.display = 'none';
          if (prevBtn) prevBtn.style.display = 'none';
          if (counter) counter.style.display = 'none';
        } else {
          if (nextBtn) nextBtn.style.display = 'flex';
          if (prevBtn) prevBtn.style.display = 'flex';
          if (counter) counter.style.display = 'block';
        }

        if (currentEl) currentEl.textContent = this.currentIndex + 1;
        if (totalEl) totalEl.textContent = total;
      }
    }
  );
}
